/**
 * クリップボードコピー（data 属性駆動）
 *
 * マークアップ規約（どちらか一方を指定）:
 *   <button data-lms-copy="コピーしたい文字列">コピー</button>
 *   <button data-lms-copy-target="#password">コピー</button>   ← 対象要素のテキスト/値をコピー
 *
 * 任意属性:
 *   data-lms-copy-message="コピーしました"   ← 成功トーストの文言（既定は「コピーしました」）
 *   data-lms-copy-label="コピー完了"          ← 成功時に一時的に差し替えるボタン文言（2000ms 後に復帰）
 */
import { showToast } from "./toast.js";

const LABEL_RESTORE_MS = 2000;

function resolveText(trigger: HTMLElement): string | null {
  const direct = trigger.dataset["lmsCopy"];
  if (direct !== undefined) return direct;

  const selector = trigger.dataset["lmsCopyTarget"];
  if (!selector) return null;

  const target = document.querySelector(selector);
  if (!target) return null;
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
    return target.value;
  }
  return target.textContent ?? "";
}

/** navigator.clipboard が使えない環境（HTTP 経由など）向けのフォールバック */
function legacyCopy(text: string): boolean {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  let ok: boolean;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  ta.remove();
  return ok;
}

async function copy(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // 権限拒否などの場合はフォールバックに落とす
    }
  }
  return legacyCopy(text);
}

export function initClipboard(scope: ParentNode = document): void {
  scope
    .querySelectorAll<HTMLElement>("[data-lms-copy], [data-lms-copy-target]")
    .forEach((trigger) => {
      if (trigger.dataset["lmsCopyReady"] === "1") return;
      trigger.dataset["lmsCopyReady"] = "1";

      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        const text = resolveText(trigger);
        if (text === null) return;

        void copy(text).then((ok) => {
          if (!ok) {
            showToast("コピーに失敗しました", "error");
            return;
          }
          showToast(trigger.dataset["lmsCopyMessage"] ?? "コピーしました", "success");

          const doneLabel = trigger.dataset["lmsCopyLabel"];
          const labelEl = trigger.querySelector<HTMLElement>("[data-lms-copy-label-slot]") ?? trigger;
          if (doneLabel && labelEl.dataset["lmsCopyRestoring"] !== "1") {
            const original = labelEl.textContent ?? "";
            labelEl.dataset["lmsCopyRestoring"] = "1";
            labelEl.textContent = doneLabel;
            window.setTimeout(() => {
              labelEl.textContent = original;
              delete labelEl.dataset["lmsCopyRestoring"];
            }, LABEL_RESTORE_MS);
          }
        });
      });
    });
}
